import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Link as MuiLink,
  Paper,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { LockOutlined, Visibility, VisibilityOff, ArrowBack } from '@mui/icons-material';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import PasswordStrengthMeter from '../../components/Auth/PasswordStrengthMeter';
import { evaluatePassword } from '../../utils/passwordStrength';

/**
 * Accept portal invite magic link — sets a permanent password via /api/portal/accept-invite.
 */
const PortalSetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const strength = evaluatePassword(password);
  const canSubmit =
    Boolean(token) &&
    strength.valid &&
    password === confirmPassword &&
    !isLoading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!token) {
      setError('Invite link is missing or invalid. Ask your firm to resend the invite.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!strength.valid) {
      setError(strength.errors.join('. ') || 'Password does not meet requirements');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch('/api/portal/accept-invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(json.message || 'Unable to set password');
      }
      setDone(true);
      toast.success('Password set — you can sign in');
    } catch (err: any) {
      setError(err.message || 'Unable to set password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.100',
        padding: 2,
      }}
    >
      <Paper elevation={4} sx={{ width: '100%', maxWidth: 440, borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', padding: 3, textAlign: 'center' }}>
          <LockOutlined sx={{ fontSize: 40, mb: 1 }} />
          <Typography variant="h5" fontWeight={600}>
            Set your portal password
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
            Complete your Client Portal invite
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          {!token && (
            <Alert severity="error" sx={{ mb: 2 }}>
              This invite link is missing a token. Open the link from your email, or ask your firm to
              resend the invite.
            </Alert>
          )}

          {done ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                Your password is set. Sign in with your email and new password.
              </Alert>
              <Button fullWidth variant="contained" onClick={() => navigate('/portal/login')}>
                Go to portal login
              </Button>
            </Box>
          ) : (
            <form onSubmit={handleSubmit}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              <TextField
                fullWidth
                label="New password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword((v) => !v)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <PasswordStrengthMeter password={password} />
              <TextField
                fullWidth
                label="Confirm password"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                margin="normal"
                required
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={!canSubmit}
                sx={{ mt: 2, mb: 1 }}
              >
                {isLoading ? <CircularProgress size={22} color="inherit" /> : 'Set password'}
              </Button>
              <Box sx={{ textAlign: 'center', mt: 1 }}>
                <MuiLink
                  component={Link}
                  to="/portal/login"
                  underline="hover"
                  variant="body2"
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                >
                  <ArrowBack fontSize="inherit" /> Back to portal login
                </MuiLink>
              </Box>
            </form>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default PortalSetPasswordPage;
